/*
 * /patient/appointments — patient-facing appointments list (read-only)
 */
import {
	Button,
	cn,
	Container,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	Page,
	PageHeader,
	Placeholder,
} from "@emr/react-ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { CalendarClock, Video } from "lucide-react"
import { useState } from "react"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"
import { PatientPortalFooter } from "../../components/patient-portal-footer"

export const Route = createFileRoute("/patient/appointments")({
	component: PatientAppointmentsPage,
	loader: rolePageLoader(["patient"]),
})

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
	scheduled: { label: "Scheduled", className: "bg-blue-100  text-blue-700  border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800" },
	confirmed:  { label: "Confirmed", className: "bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800" },
	cancelled:  { label: "Cancelled", className: "bg-red-100   text-red-600   border-red-200   dark:bg-red-950  dark:text-red-300  dark:border-red-800"  },
	completed:  { label: "Completed", className: "bg-gray-100  text-gray-600  border-gray-200  dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700"  },
	no_show:    { label: "No Show",   className: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800" },
}

type Appt = {
	id: string
	startsAt: Date | string
	endsAt: Date | string
	timezone: string
	status: string
	type?: string | null
	reason?: string | null
	practitionerId?: string | null
}


function PatientAppointmentsPage() {
	const navigate = useNavigate()
	const [selected, setSelected] = useState<Appt | null>(null)

	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { active: true } })
	const { data: session } = trpc.telemedicine.getActiveByPatient.useQuery(
		{ patientId: patient?.id ?? "" },
		{ enabled: !!patient?.id, refetchInterval: 10000 },
	)

	const { data: appointments = [], isLoading } = trpc.appointment.getAllByFilter.useQuery(
		{ where: { patientId: patient?.id ?? "", active: true } },
		{ enabled: !!patient?.id },
	)

	const { data: practitioners = [] } = trpc.practitioner.getAllByFilter.useQuery(
		{ where: { active: true } },
		{ enabled: !!patient?.id },
	)

	const sorted = [...(appointments ?? [])].sort(
		(a, b) => new Date(b.startsAt as any).getTime() - new Date(a.startsAt as any).getTime(),
	)

	const selectedPrac = selected
		? (practitioners ?? []).find((p) => p.id === selected.practitionerId)
		: null

	// Session is active when there is a live telemedicine meeting ready to join
	const hasActiveSession = !!session

	return (
		<Page>
			<Container>
				<PageHeader
					title="My Appointments"
					right={
						hasActiveSession
							? {
									Component: (
										<button
											onClick={() => navigate({ to: "/patient/session" })}
											className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-500 transition-colors animate-pulse"
										>
											<Video className="w-4 h-4" />
											Join Video Visit
										</button>
									),
								}
							: undefined
					}
				/>

				{isLoading ? (
					<Placeholder title="Loading appointments…" />
				) : sorted.length === 0 ? (
					<Placeholder title="No appointments on record" />
				) : (
					<div className="flex flex-col gap-3">
						{sorted.map((appt) => {
							const prac = (practitioners ?? []).find((p) => p.id === appt.practitionerId)
							const badge = STATUS_BADGE[appt.status] ?? STATUS_BADGE.scheduled
							const isLinkedSession =
								hasActiveSession &&
								(session as any)?.appointmentId === appt.id
							return (
								<button
									key={appt.id}
									onClick={() => setSelected(appt as Appt)}
									className={cn(
										"flex items-start justify-between gap-4 rounded-lg border bg-card px-5 py-4 text-left w-full transition-colors",
										"hover:bg-accent/50 cursor-pointer",
										isLinkedSession && "ring-2 ring-green-500/60",
									)}
								>
									<div className="flex items-start gap-3">
										<CalendarClock className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
										<div className="flex flex-col gap-1">
											<p className="text-sm font-medium">
												{formatApptTime(appt.startsAt, appt.endsAt, appt.timezone)}
											</p>
											{prac && (
												<p className="text-xs text-muted-foreground">
													{prac.firstName} {prac.lastName}
													{prac.professionalDesignation ? `, ${prac.professionalDesignation}` : ""}
												</p>
											)}
											{appt.type && (
												<p className="text-xs text-muted-foreground capitalize">
													{appt.type.replace("_", " ")}
												</p>
											)}
											{appt.reason && (
												<p className="text-xs italic text-muted-foreground">"{appt.reason}"</p>
											)}
										</div>
									</div>
									<div className="flex flex-col items-end gap-2 shrink-0">
										<span
											className={cn(
												"inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
												badge.className,
											)}
										>
											{badge.label}
										</span>
										{isLinkedSession && (
											<span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
												<Video className="size-3" />
												Live
											</span>
										)}
									</div>
								</button>
							)
						})}
					</div>
				)}

				<PatientPortalFooter />
			</Container>

			{/* Appointment detail dialog */}
			<Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null) }}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle>Appointment Details</DialogTitle>
					</DialogHeader>

					{selected && (
						<div className="flex flex-col gap-4">
							<div className="rounded-md bg-muted/40 px-4 py-3 text-sm space-y-2">
								<DetailRow label="Date & Time" value={formatApptTime(selected.startsAt, selected.endsAt, selected.timezone)} />
								{selectedPrac && (
									<DetailRow
										label="Provider"
										value={`${selectedPrac.firstName} ${selectedPrac.lastName}${selectedPrac.professionalDesignation ? `, ${selectedPrac.professionalDesignation}` : ""}`}
									/>
								)}
								{selected.type && (
									<DetailRow label="Type" value={selected.type.replace("_", " ")} capitalize />
								)}
								{selected.reason && (
									<DetailRow label="Reason" value={`"${selected.reason}"`} />
								)}
								<DetailRow
									label="Status"
									value={STATUS_BADGE[selected.status]?.label ?? selected.status}
								/>
							</div>

							{hasActiveSession && (
								<Button
									className="w-full gap-2 bg-green-600 hover:bg-green-500 text-white animate-pulse"
									onClick={() => {
										setSelected(null)
										navigate({ to: "/patient/session" })
									}}
								>
									<Video className="size-4" />
									Join Video Visit
								</Button>
							)}

							<Button variant="outline" className="w-full" onClick={() => setSelected(null)}>
								Close
							</Button>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</Page>
	)
}

function DetailRow({
	label,
	value,
	capitalize,
}: {
	label: string
	value: string
	capitalize?: boolean
}) {
	return (
		<div className="flex gap-2">
			<span className="text-muted-foreground min-w-[72px] shrink-0">{label}</span>
			<span className={cn("font-medium", capitalize && "capitalize")}>{value}</span>
		</div>
	)
}

function formatApptTime(startsAt: Date | string, endsAt: Date | string, tz: string): string {
	const s = new Date(startsAt as any)
	const e = new Date(endsAt as any)
	const date = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz, weekday: "short", month: "short", day: "numeric", year: "numeric",
	}).format(s)
	const start = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true,
	}).format(s)
	const end = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short",
	}).format(e)
	return `${date} · ${start} – ${end}`
}
